
















































































import { Component, Emit, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import PickerDialogComponent from "@/components/common/PickerDialog.vue";
import { ContactPersonInfo, FIRST_CONTACT_PERSON_ID, FIRST_ROOM_ID, isPortalOnlyProject, ProjectDetails, ProjectSyncTarget } from "@/data/models/ProjectDetails";
import { createProjectNoteLog, formatApartmentName, formatContactPersonName, formatRoomName, NoteApartmentInfo, NoteRoomInfo, ProjectNoteLog, resolveNoteRoomInfo } from "@/data/models/ProjectLog";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { isLeakDetectionProject } from "@/data/repository/ProjectRepository";
import { getRoomUsageOptions, loadRoomUsageOptions } from "@/data/repository/RoomUsageOptionsRepository";
import { deleteProjectNoteLog, saveProjectNoteLog, saveSyncedProjectNote } from "@/data/repository/ProjectLogRepository";
import { NoteChange } from "@/data/models/ProjectNote";

interface InitialProjectNoteLog {
  targetApartment: NoteApartmentInfo | null;
  targetRoom: NoteRoomInfo | null;
  useForReport: boolean | null;
  note: string | null;
}

@Component({
  methods: {},
  components: { 
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    picker: PickerDialogComponent,
  },
})
export default class ProjectNoteDialog extends Vue {
  @Ref("baseDialog")
  baseDialog: BaseDialogComponent;

  @Ref("confirm")
  confirmationDialogComponent!: ConfirmationDialogComponent;

  @Prop()
  projectDetails!: ProjectDetails;

  @Prop()
  contactPersons!: ContactPersonInfo[];

  @Prop({ default: false })
  isReadOnlyMode!: boolean;

  noteId: string | null = null;
  targetApartment: NoteApartmentInfo | null = null;
  targetRoom: NoteRoomInfo | null = null;
  useForReport: boolean | null = null;
  note: string | null = null;

  isEditing = false;
  isNewNote = false;
  isLoading = false;

  title = "";

  initialProjectNoteLog: InitialProjectNoteLog = this.createDefaultProjectNoteLog();

  @Emit("onSuccess")
  onSuccess(message: string) {
    this.$emit("onSuccess", message);
    this.stopLoading();
  }

  @Emit("onError")
  onError(message: string) {
    this.$emit("onError", message);
    this.stopLoading();
  }

  @Watch("targetApartment")
  onTargetApartmentChanged() {
    if (!this.targetApartment || this.targetApartment.contactPersonId !== this.targetRoom?.contactPersonId) {
      this.targetRoom = null;
    }
  }

  @Watch("targetRoom")
  onTargetRoomChanged() {
    if (this.targetRoom) {
      this.targetApartment = this.apartmentOptions.find(apartment => apartment.contactPersonId === this.targetRoom?.contactPersonId) ?? null;
    }
  }

  @Watch("hasDetailsEdited")
  onDetailsChanged() {
    this.baseDialog.negativeButtonText = this.calculateNegativeButtonText();
    this.baseDialog.showNeutralPositiveButton = this.calculateNeutralPositiveButtonText() ? true : false;
  }

  get hideContactPersonNumber() {
    return this.contactPersons?.length === 1;
  }

  get apartmentFormatter() {
    return (noteApartmentInfo: NoteApartmentInfo) => {
      return formatApartmentName(
        this, 
        noteApartmentInfo,
        this.contactPersons,
        this.hideContactPersonNumber
      );
    };
  }

  get shouldDisableApartmentSelection() {
    return this.apartmentOptions.length < 2;
  }

  get shouldDisableRoomSelection() {
    return this.roomOptions.length === 0;
  }

  get apartmentOptions() {
    if (!this.contactPersons) {
      return [{
        contactPersonId: FIRST_CONTACT_PERSON_ID,
        apartmentName: this.$t("projectLogs.noteLogs.defaultApartmentName").toString()
      }] as NoteApartmentInfo[];
    }

    return this.contactPersons
      .sort((cp1, cp2) => cp1.contactPersonNumber - cp2.contactPersonNumber)
      .map((cp) => {
        return {
          contactPersonId: cp.id,
          apartmentName: formatContactPersonName(this, cp, false, this.hideContactPersonNumber)
        } as NoteApartmentInfo;
      })
  }

  get showSelectRoomOptions() {
    return !isPortalOnlyProject(this.projectDetails) && !isLeakDetectionProject(this.projectDetails.id);
  }

  get projectRooms() {
    return this.projectDetails.damagePatternData?.rooms?.filter(room => room.roomNumber !== 0);
  }

  /** The Beschreibung version a room usage picked from the list is read against; only shown rooms need it. */
  get roomUsageOptionsVersion() {
    return this.showSelectRoomOptions ? this.projectDetails.damagePatternData?.roomInfoConfigVersion ?? null : null;
  }

  get roomUsageOptions() {
    return getRoomUsageOptions(this.roomUsageOptionsVersion);
  }

  @Watch("roomUsageOptionsVersion", { immediate: true })
  onRoomUsageOptionsVersionChanged() {
    loadRoomUsageOptions(this.roomUsageOptionsVersion);
  }

  get projectSyncTarget() {
    return this.projectDetails.syncTarget;
  }

  get readonly() {
    return !this.isEditing;
  }

  get roomOptions() {
    if (!this.projectRooms) {
      const defaultRoomNumber = 1;

      return [{
        contactPersonId: FIRST_CONTACT_PERSON_ID,
        roomId: FIRST_ROOM_ID,
        roomNumber: defaultRoomNumber,
        roomName: formatRoomName(this, defaultRoomNumber)
      }];
    }

    return this.projectRooms
      .filter(room => !this.targetApartment || room.contactPersonId === this.targetApartment.contactPersonId)
      .sort((room1, room2) => room1.roomNumber - room2.roomNumber)
      .map((room) => {
        return {
          contactPersonId: room.contactPersonId,
          roomId: room.id,
          roomNumber: room.roomNumber,
          roomName: formatRoomName(this, room.roomNumber, room, this.roomUsageOptions)
        } as NoteRoomInfo;
      })
  }

  get roomFormatter() {
    return (noteRoomInfo: NoteRoomInfo) => {
      return noteRoomInfo.roomName;
    };
  }

  calculatePositiveButtonText() {
    if (this.isReadOnlyMode) {
      return "";
    }

    return this.$t(`common.${this.isEditing
      ? 'save'
      : 'edit'
    }`).toString();
  }

  calculateNeutralPositiveButtonText() {
    if (this.isNewNote || !this.isEditing || this.hasDetailsEdited) {
      return undefined;
    }

    return this.$t(`common.delete`).toString();
  }

  calculateNegativeButtonText() {
    if (this.isNewNote) {
      return this.$t(`common.${this.hasDetailsEdited
        ? 'abort'
        : 'close'
      }`).toString(); 
    }

    return this.$t(`common.${this.isEditing && this.hasDetailsEdited
      ? 'abort'
      : 'close'
    }`).toString(); 
  }
  
  compareNoteApartmentInfo(
    info1: NoteApartmentInfo | null, 
    info2: NoteApartmentInfo | null
  ) {
    return info1?.apartmentName === info2?.apartmentName &&
      info1?.contactPersonId === info2?.contactPersonId;
  }

  compareNoteRoomInfo(
    info1: NoteRoomInfo | null, 
    info2: NoteRoomInfo | null
  ) {
    return info1?.contactPersonId === info2?.contactPersonId &&
      info1?.roomId === info2?.roomId &&
      info1?.roomName === info2?.roomName &&
      info1?.roomNumber === info2?.roomNumber;
  }

  createDefaultProjectNoteLog() {
    return {
      targetApartment: null,
      targetRoom: null,
      useForReport: null,
      note: null
    } as InitialProjectNoteLog;
  }

  initializeProjectNoteLog(projectNoteLog: ProjectNoteLog | null) {
    this.targetApartment = projectNoteLog?.targetApartment ?? null;
    // The stored room label is refreshed first, so view, change detection and the next save all
    // use the room as it is now, see resolveNoteRoomInfo.
    this.targetRoom = projectNoteLog?.targetRoom
      ? resolveNoteRoomInfo(this, projectNoteLog.targetRoom, this.projectRooms ?? [], this.roomUsageOptions)
      : null;
    this.useForReport = projectNoteLog?.useForReport ?? null;
    this.note = projectNoteLog?.note ?? null;
    this.noteId = projectNoteLog?.id ?? null;

    this.initialProjectNoteLog = {
      targetApartment: this.targetApartment,
      targetRoom: this.targetRoom,
      useForReport: this.useForReport,
      note: this.note
    };
  }

  get hasDetailsEdited() {
    return !this.compareNoteApartmentInfo(this.targetApartment, this.initialProjectNoteLog.targetApartment) ||
      !this.compareNoteRoomInfo(this.targetRoom, this.initialProjectNoteLog.targetRoom) ||
      this.useForReport !== this.initialProjectNoteLog.useForReport ||
      this.note !== this.initialProjectNoteLog.note;
  }

  async show(options: ProjectNoteDialogOptions) {
    this.isNewNote = options.isNewNote ?? false;
    this.isEditing = this.isNewNote;
    this.title = this.$t(`projectLogs.noteLogs.dialog.title${this.isNewNote 
      ? 'Create' 
      : 'Edit'
    }`).toString();

    if (options.projectNoteLog) {
      // The saved room is relabelled on opening, so the usage options must be there first. Usually
      // instant: the component loaded them on mount.
      await loadRoomUsageOptions(this.roomUsageOptionsVersion);
      this.initializeProjectNoteLog(options.projectNoteLog);
    }

    this.baseDialog.show({
      title: this.title,
      positiveButtonText: this.calculatePositiveButtonText(),
      neutralPositiveButtonText: this.calculateNeutralPositiveButtonText(),
      negativeButtonText: this.calculateNegativeButtonText(),
      closeOnSave: false,
      closeOnNeutralPositiveAction: false,
      hidePositiveButtonIfEmpty: true,
      hideSaveButton: this.isReadOnlyMode,
    });
    
    this.preselectApartmentIfNecessary();

    if (this.isNewNote) {
      this.onStartEdit();
    }
  }

  preselectApartmentIfNecessary() {
    const defaultTargetApartment = 
      (
        this.shouldDisableApartmentSelection && 
        this.apartmentOptions.length === 1
      ) ||
      (
        !this.isNewNote &&
        this.targetApartment &&
        this.apartmentOptions.length > 0
      )
        ? this.apartmentOptions[0]
        : null;
    
    this.targetApartment = defaultTargetApartment;
    this.initialProjectNoteLog.targetApartment = this.targetApartment;
  }

  onPositiveButtonClicked() {
    if (!this.isEditing) {
      this.onStartEdit();
    } else if (this.isEditing) {
      this.onSubmit();
    }
  }

  async onNeutralPositiveButtonClicked() {
    if (!this.noteId) {
      return;
    }

    const shouldDeleteNoteLog = await this.confirmationDialogComponent.show({
      title: this.$t("projectLogs.noteLogs.dialog.deletionConfirmationTitle").toString(),
      message: this.$t("projectLogs.noteLogs.dialog.deletionConfirmationMessage").toString(),
    });

    if (shouldDeleteNoteLog) {
      const result = this.projectSyncTarget
        ? await saveSyncedProjectNote(
            null,
            this.projectDetails.id,
            this.noteId,
            NoteChange.DELETED,
            this.projectSyncTarget
          )
        : await deleteProjectNoteLog(
            this.projectDetails.id,
            this.noteId
          );

      if (!result) {
        this.onError("projectLogs.noteLogs.dialog.deletionErrorMessage");

        return;
      }

      this.onSuccess("projectLogs.noteLogs.dialog.deletionSuccessMessage");
    }
  }

  onStartEdit() {
    this.isEditing = true;

    this.baseDialog.positiveButtonText = this.calculatePositiveButtonText();
    this.baseDialog.negativeButtonText = this.calculateNegativeButtonText();

    const neutralPositiveButtonText = this.calculateNeutralPositiveButtonText();
    if (neutralPositiveButtonText) {
      this.baseDialog.neutralPositiveButtonText = neutralPositiveButtonText;
    }
  }

  async onSubmit() {
    this.startLoading();

    const projectNoteLog = createProjectNoteLog(
      this.targetApartment,
      this.targetRoom,
      this.useForReport ?? false,
      this.note ?? undefined,
      this.noteId ?? undefined
    );
   
    const result = this.projectSyncTarget
      ? await saveSyncedProjectNote(
          projectNoteLog,
          this.projectDetails.id,
          projectNoteLog.id,
          this.isNewNote ? NoteChange.CREATED : NoteChange.UPDATED,
          this.projectSyncTarget 
        )
      : await saveProjectNoteLog(
          this.projectDetails.id, 
          projectNoteLog
        );

    if (!result) {
      this.onError(this.isNewNote
        ? "projectLogs.noteLogs.dialog.createErrorMessage"
        : "projectLogs.noteLogs.dialog.editErrorMessage"
      );

      return;
    }

    this.onSuccess(this.isNewNote
      ? "projectLogs.noteLogs.dialog.createSuccessMessage"
      : "projectLogs.noteLogs.dialog.editSuccessMessage"
    );

    this.resetDialog();
  }

  async onAbortEditNote() {
    if (this.isEditing) {
      if (this.hasDetailsEdited) {
        const result = await this.confirmationDialogComponent.show({
          title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
          message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
          rejectMessage: this.$t("common.back").toString(),
          acceptMessage: this.$t("common.abort").toString(),
        });

        if (!result) {
          return;
        }
      }
    }

    this.resetDialog();
  }

  resetDialog() {
    this.baseDialog.isShowing = false;
    this.isEditing = false;
    this.isNewNote = false;
    
    this.initializeProjectNoteLog(null);
  }

  startLoading() {
    this.isLoading = true;
  }

  stopLoading() {
    this.isLoading = false;
  }
}

interface ProjectNoteDialogOptions {
  isNewNote: boolean;
  projectNoteLog?: ProjectNoteLog;
  syncTarget?: ProjectSyncTarget;
}
